
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageCard } from './PackageCard';
import { FilterSidebar } from './FilterSidebar';
import { CartOffcanvas } from './CartOffcanvas';
import { useCart } from '../../../hooks/useCart';
import useToast from '../../../hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import * as groupToursService from '@/services/groupTours';
import { getGroupTourImageUrl } from '@/environments/environment';

export interface GroupPackage1 {
  id?: string;
  _id?: string;
  tourTitle: string;
  tags?: string[];
  imageUrl?: string;
  reviews?: number;
  rating?: number;
  inclusive?: string[];
  description: string;
  price?: {
    currency?: string;
    total?: number;
    emi?: number;
    emiFrequency?: string;
    personType?: string;
  };
  destinations?: {
    days?: number;
    countries?: {
      name: string;
      city: string[];
    }[];
  };
  depatures?: { city: string; date: string }[];
  highlights?: string[];
  category: string;
  itinerary?: Array<{
    day: number;
    title: string;
    description: string;
  }>;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}



export const PackagesSection: React.FC = () => {
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 900000]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('');
  const [isCartOpen, setIsCartOpen] = useState(false);


  const { cartItems, addToCart, removeFromCart, updateQuantity, getCartTotal, getCartItemsCount } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [fetchError, setFetchError] = useState<string | null>(null);

  const { data: fetchedPackages = [], isLoading } = useQuery<any[]>({
    queryKey: ['/v1/group-tours/public'],
    queryFn: async () => {
      try {
        const res = await groupToursService.listGroupTours();
        const extracted = res.results || res.data || res || [];
        setFetchError(null);
        return extracted;
      } catch (err: any) {
        const msg = err instanceof Error ? err.message : String(err);
        setFetchError(msg);
        toast({ title: 'Failed to load packages', description: msg, variant: 'destructive' });
        return [];
      }
    },
  });

  // Source is only the DB results (do not include local JSON samples on the public page)
  const sourceWithFallback = useMemo(() => fetchedPackages || [], [fetchedPackages]);

  const categories = Array.from(new Set(sourceWithFallback.map((pkg: any) => pkg.category)));

  const filteredPackages = useMemo(() => {
    const source = sourceWithFallback || [];
    let filtered = source.filter((pkg: any) => {
      const withinPriceRange = (pkg.price?.total || 0) >= priceRange[0] && (pkg.price?.total || 0) <= priceRange[1];
      const matchesCategory = !selectedCategory || pkg.category === selectedCategory;
      return withinPriceRange && matchesCategory;
    });

    if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price.total - b.price.total);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price.total - a.price.total);
    } else if (sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'popular') {
      filtered.sort((a, b) => b.reviews - a.reviews);
    }

    return filtered;
  }, [priceRange, selectedCategory, sortBy, sourceWithFallback]);

  const handleAddToCart = (pkg: GroupPackage1) => {
    const id = (pkg as any).id || (pkg as any)._id;
    const image = getGroupTourImageUrl(pkg.imageUrl) || '';
    addToCart({
      id,
      title: pkg.tourTitle,
      price: pkg.price?.total || 0,
      image,
      category: pkg.category,
      itinerary: pkg.itinerary,
    });
    // show confirmation toast and navigate to details with location as title
    toast({ title: 'Added to cart', description: pkg.tourTitle });
    navigate(`/group-tours/details?id=${encodeURIComponent(id)}&location=${encodeURIComponent(pkg.tourTitle)}`);
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex gap-8">
          {/* Filter Sidebar */}
          <FilterSidebar
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
          />

          {/* Main Content */}
          <div className="flex-1">
            {fetchError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                <strong>Could not load packages from API:</strong> {fetchError}
              </div>
            )}
            {/* Header with Sort and Cart */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Group Tour Packages</h2>
                <p className="text-gray-600">{filteredPackages.length} packages found</p>
              </div>

              <div className="flex items-center gap-4">
                {/* Sort Dropdown */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Sort by</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="popular">Most Popular</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>

                {/* Cart Button */}
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line text-lg"></i>
                  Cart
                  {getCartItemsCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getCartItemsCount()}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Package Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPackages.map((pkg: any) => (
                <PackageCard
                  key={pkg.id || pkg._id}
                  package={pkg}
                />
              ))}
            </div>

            {isLoading ? (
              <div className="text-center py-12">Loading packages...</div>
            ) : filteredPackages.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No packages found</h3>
                <p className="text-gray-500">Try adjusting your filters to see more results.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cart Offcanvas */}
      <CartOffcanvas
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onRemoveItem={removeFromCart}
        onUpdateQuantity={updateQuantity}
        cartTotal={getCartTotal()}
      />
    </section>
  );
};
